import {useCallback, useEffect, useRef} from "react";
import {fromEvent, interval, merge, Subject, EMPTY} from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  map,
  throttleTime,
} from "rxjs/operators";
import {useApi} from "./useApi";
import {getToday} from "../../../utils/datetime";
import getFurthestPosition from "../utils/getFurthestPosition";
import {addWatchedRange} from "../utils/watchedRanges";

export const useSync = ({
  user,
  activityRef,
  setActivity,
  historyRef,
  setWatchHistory,
}) => {
  const lastSyncDataRef = useRef({activity: {}, history: {}});
  const statEventsRef = useRef(null);
  const videoProgressEventsRef = useRef(null);
  const {updateActivityAndHistory} = useApi();

  if (!statEventsRef.current) {
    statEventsRef.current = new Subject();
  }

  if (!videoProgressEventsRef.current) {
    videoProgressEventsRef.current = new Subject();
  }

  const applyDailyStatUpdate = useCallback(
    (key, increment = 1) => {
      const today = getToday();

      setActivity((prev) => {
        const todayStats = prev[today] || {
          mouseMoves: 0,
          clicks: 0,
          secondsActive: 0,
          totalVideoSeconds: 0,
        };

        return {
          ...prev,
          [today]: {
            ...todayStats,
            [key]: (todayStats[key] || 0) + increment,
          },
        };
      });
    },
    [setActivity],
  );

  const updateDailyStat = useCallback((key, increment = 1) => {
    statEventsRef.current.next({key, increment});
  }, []);

  // `from` is where this stretch of playback started (the previous tick's
  // position). The player only reports a tick for normal playback, never for
  // a seek, so [from, currentTime] is a stretch that actually played.
  const handleVideoProgress = useCallback((id, currentTime, delta, from) => {
    videoProgressEventsRef.current.next({
      id,
      currentTime,
      delta: delta || 0,
      from,
    });
  }, []);

  useEffect(() => {
    const subscription = statEventsRef.current.subscribe(({key, increment}) => {
      applyDailyStatUpdate(key, increment);
    });

    return () => subscription.unsubscribe();
  }, [applyDailyStatUpdate]);

  useEffect(() => {
    const subscription = videoProgressEventsRef.current.subscribe(
      ({id, currentTime, delta, from}) => {
        const today = getToday();

        setWatchHistory((prev) => {
          const videoEntry = prev[id] || {
            lastPosition: 0,
            dailyBreakdown: {},
          };

          // Monotonic high-water mark of playback position. lastPosition is
          // the resume point and legitimately moves backwards on rewatch or
          // scrub-back; completion status derives from maxPosition instead so
          // a Completed/Watched badge can never regress (falling back to
          // lastPosition seeds entries that predate this field). The
          // server-side merge (activityMerge) keeps it max-of-all-devices.
          const maxPosition = Math.max(
            Number(videoEntry.maxPosition) || 0,
            Number(videoEntry.lastPosition) || 0,
            Number(currentTime) || 0,
          );

          // Entries from before watchedRanges existed carry their old
          // furthest-position progress over as [0, furthest], so a video
          // already shown as watched stays watched (getWatchedProgress).
          const priorRanges = Array.isArray(videoEntry.watchedRanges)
            ? videoEntry.watchedRanges
            : prev[id] && getFurthestPosition(videoEntry) > 0
              ? [[0, getFurthestPosition(videoEntry)]]
              : [];
          const played =
            delta > 0 &&
            Number(from) >= 0 &&
            Number(currentTime) > Number(from);
          const watchedRanges = played
            ? addWatchedRange(priorRanges, Number(from), Number(currentTime))
            : priorRanges;

          return {
            ...prev,
            [id]: {
              lastPosition: currentTime,
              maxPosition,
              watchedRanges,
              // Precise per-video timestamp of the most recent play. The
              // Recently Watched sidebar orders by this. dailyBreakdown is
              // only day-level, so several videos watched the same day tied
              // on date -- newly watched videos then neither sorted to the
              // top nor survived the top-5 cut (QA: list order wrong / new
              // videos not added). Persisted via update-contact/activity.
              lastWatched: new Date().toISOString(),
              dailyBreakdown: {
                ...videoEntry.dailyBreakdown,
                [today]: (videoEntry.dailyBreakdown[today] || 0) + delta,
              },
            },
          };
        });

        if (delta > 0) {
          statEventsRef.current.next({
            key: "totalVideoSeconds",
            increment: delta,
          });
        }
      },
    );

    return () => subscription.unsubscribe();
  }, [setWatchHistory]);

  useEffect(() => {
    const heartbeat$ = interval(1000).pipe(
      map(() => ({
        key: "secondsActive",
        increment: 1,
      })),
    );

    const mouseMove$ = fromEvent(window, "mousemove", {passive: true}).pipe(
      throttleTime(1000),
      map(() => ({
        key: "mouseMoves",
        increment: 1,
      })),
    );

    const click$ = fromEvent(window, "click").pipe(
      map(() => ({
        key: "clicks",
        increment: 1,
      })),
    );

    const subscription = merge(heartbeat$, mouseMove$, click$).subscribe(
      (event) => {
        statEventsRef.current.next(event);
      },
    );

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.customer_id) return;

    const sync$ = interval(10000).pipe(
      filter(() => {
        const hasActivityChanged =
          JSON.stringify(activityRef.current) !==
          JSON.stringify(lastSyncDataRef.current.activity);

        const hasHistoryChanged =
          JSON.stringify(historyRef.current) !==
          JSON.stringify(lastSyncDataRef.current.history);

        return hasActivityChanged || hasHistoryChanged;
      }),
      exhaustMap(() =>
        updateActivityAndHistory({
          user,
          activity: activityRef.current,
          watchHistory: historyRef.current,
        })
          .then((data) => {
            lastSyncDataRef.current.activity = JSON.parse(
              JSON.stringify(activityRef.current),
            );
            lastSyncDataRef.current.history = JSON.parse(
              JSON.stringify(historyRef.current),
            );

            return data;
          })
          .catch((err) => {
            console.error("Sync failed", err);
            return null;
          }),
      ),
      catchError((err) => {
        console.error("Sync stream failed", err);
        return EMPTY;
      }),
    );

    const subscription = sync$.subscribe();

    return () => subscription.unsubscribe();
  }, [user, activityRef, historyRef, updateActivityAndHistory]);

  useEffect(() => {
    if (!user?.customer_id) return;

    // Persist the latest stats when the tab is closed, navigated away from,
    // hidden, or backgrounded. `beforeunload` alone is NOT reliable for an
    // actual tab close (browsers, especially mobile, frequently skip it and
    // may kill the async request), which is why session progress was lost on
    // close/reopen. `visibilitychange` -> "hidden" is the signal browsers
    // guarantee before freezing/discarding a tab, and `pagehide` covers
    // bfcache navigations; flushing on all three makes the final save land.
    // The keepalive flag lets the request outlive the document teardown.
    const flush = () => {
      updateActivityAndHistory({
        user,
        activity: activityRef.current,
        watchHistory: historyRef.current,
        keepalive: true,
      });
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };

    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [user, activityRef, historyRef, updateActivityAndHistory]);

  return {
    updateDailyStat,
    handleVideoProgress,
  };
};
